import { Check, ChevronDown, LogOut, MessageSquare } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useSendFeedback } from "~/common/hooks";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui";
import { useAuth } from "~/features/authn/context";
import { useTenant } from "~/features/tenant/context";
import { useTenantSwitcher } from "~/features/tenant/hooks";
import { type Locale, persistLocale, SUPPORTED_LOCALES } from "~/i18n/resolveLocale";
import { cn } from "~/lib/utils";

interface Crumb {
  label: string;
  to: string;
  /** When false, the crumb is rendered as plain text (no landing page / dynamic id). */
  linkable?: boolean;
}

interface TopbarProps {
  crumbs: Crumb[];
}

const LOCALE_LABELS: Record<Locale, string> = {
  en: "EN",
  ja: "JA",
};

function isLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function Topbar({ crumbs }: TopbarProps) {
  const { t, i18n } = useTranslation();
  const { user, signOut } = useAuth();
  const { tenantShortCode } = useTenant();
  const { sendFeedback, isAvailable: isFeedbackAvailable } = useSendFeedback();
  const { switchToTenant } = useTenantSwitcher();
  const displayName = user?.displayName ?? user?.email ?? "—";
  const initials = (user?.displayName ?? user?.email ?? "??").slice(0, 2).toUpperCase();
  const photoURL = user?.photoURL ?? undefined;
  const tenants = user?.tenants ?? [];
  const currentLocale: Locale = isLocale(i18n.resolvedLanguage ?? i18n.language)
    ? ((i18n.resolvedLanguage ?? i18n.language) as Locale)
    : "en";

  const onLocaleSelect = useCallback(
    (locale: Locale) => {
      if (locale === currentLocale) return;
      persistLocale(locale);
      void i18n.changeLanguage(locale);
    },
    [i18n, currentLocale],
  );

  return (
    <header className="sticky top-0 z-10 flex h-[var(--topbar-h)] items-center gap-4 border-b border-border-default bg-bg-sticky px-6 backdrop-blur-md">
      <nav aria-label="breadcrumb" className="flex flex-1 items-center gap-1.5 text-[13px] text-fg-2">
        {crumbs.map((c, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <span key={`${c.to}-${c.label}`} className="flex items-center gap-1.5">
              {i > 0 && (
                <span aria-hidden className="text-fg-disabled">
                  /
                </span>
              )}
              {isLast ? (
                <span className="font-medium text-fg-1" aria-current="page">
                  {c.label}
                </span>
              ) : c.linkable === false ? (
                // No landing page / dynamic id: inert text rather than a broken link.
                <span className="px-0.5 text-fg-2">{c.label}</span>
              ) : (
                <Link to={c.to} className="rounded-[var(--radius-xs)] px-0.5 hover:text-fg-1 hover:underline">
                  {c.label}
                </Link>
              )}
            </span>
          );
        })}
      </nav>

      <fieldset
        className="inline-flex rounded-[var(--radius-pill)] border-0 bg-bg-subtle p-[3px] text-[11px]"
        aria-label={t("ui.language")}
      >
        {SUPPORTED_LOCALES.map((locale) => (
          <button
            key={locale}
            type="button"
            onClick={() => onLocaleSelect(locale)}
            aria-pressed={locale === currentLocale}
            className={cn(
              "rounded-[var(--radius-pill)] px-2.5 py-1 font-medium transition-colors",
              locale === currentLocale ? "bg-white text-primary-700 shadow-sm" : "bg-transparent text-fg-2",
            )}
          >
            {LOCALE_LABELS[locale]}
          </button>
        ))}
      </fieldset>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-[var(--radius-sm)] px-1.5 py-1 text-[13px] text-fg-2 hover:bg-bg-subtle hover:text-fg-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
            aria-label={displayName}
          >
            <Avatar>
              {photoURL && <AvatarImage src={photoURL} alt={displayName} />}
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <span className="hidden sm:inline">{displayName}</span>
            <ChevronDown className="h-3.5 w-3.5 text-fg-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[220px]">
          {isFeedbackAvailable && (
            <>
              <DropdownMenuItem onSelect={() => void sendFeedback()}>
                <MessageSquare />
                <span>{t("ui.sendFeedback")}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {tenants.length > 0 && (
            <>
              <DropdownMenuLabel>{t("model.tenant.modelNames")}</DropdownMenuLabel>
              {tenants.map((tenant) => {
                const isCurrent = tenant.shortCode === tenantShortCode;
                return (
                  <DropdownMenuItem
                    key={tenant.id}
                    onSelect={() => {
                      if (!isCurrent) switchToTenant(tenant.shortCode);
                    }}
                    className={cn(isCurrent && "text-primary-700 font-semibold")}
                  >
                    {isCurrent ? <Check /> : <span className="size-4" aria-hidden />}
                    <span>{tenant.shortCode}</span>
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onSelect={() => signOut()}>
            <LogOut />
            <span>{t("ui.signOut")}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
